// DataTable — TanStack Table v9 (sorting, selection with Shift-ranges, column visibility) +
// TanStack Virtual (1k+ rows) behind a small, stable column API that page builders use.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router'
import {
  useTable, tableFeatures, createColumnHelper, rowSortingFeature, createSortedRowModel, rowSelectionFeature, columnVisibilityFeature,
  type RowSelectionState, type SortingState, type ColumnVisibilityState, type Row,
} from '@tanstack/react-table'
import { useVirtualizer, observeElementOffset, observeElementRect, observeWindowOffset, observeWindowRect, elementScroll, windowScroll } from '@tanstack/react-virtual'
import { ArrowDown, ArrowUp, ArrowUpDown, Columns3, Inbox, X } from 'lucide-react'
import { cn } from './cn'
import { Checkbox } from './checkbox'
import { Skeleton, EmptyState, ErrorState } from './feedback'
import { DropdownMenu, MenuCheckboxItem, MenuLabel, MenuRadioGroup, MenuSeparator, ContextMenu, type ContextMenuItemDef } from './menu'
import { Button } from './button'
import { useLocalStorage } from '@/lib/hooks'
import { number as fmtNumber } from '@/lib/format'

export interface SortState { id: string; desc: boolean }
export type Density = 'compact' | 'comfortable'

export interface DataTableColumn<T> {
  /** Stable id; for server sorting it's the sort key sent to the API. */
  id: string
  header: ReactNode
  cell: (row: T, index: number) => ReactNode
  /** Value used for client-side sorting (enables sorting when there is no onSortChange). */
  sortValue?: (row: T) => string | number | boolean | Date | null | undefined
  /** Header is clickable to sort (server mode: requires onSortChange; client mode: requires sortValue). */
  sortable?: boolean
  /** First click sorts descending (dates, money, counts). */
  sortDescFirst?: boolean
  align?: 'left' | 'right' | 'center'
  /** CSS width ("30%", 120). */
  width?: number | string
  minWidth?: number
  /** Can be hidden from the Columns menu (default true; the first column never). */
  hideable?: boolean
  /** Hidden until the user turns it on. */
  defaultHidden?: boolean
  /** Drop the column below a breakpoint (mobile). */
  hideBelow?: 'sm' | 'md' | 'lg' | 'xl'
  /** Single-line with ellipsis once the content is wider than `maxWidth` (default 280 px). */
  truncate?: boolean
  maxWidth?: number
  className?: string
  headerClassName?: string
}

export interface BulkActionContext<T> {
  /** Selected ids — always rows that are loaded in the CURRENT result set (search/filters/view). */
  ids: string[]
  /** The selected rows themselves. */
  rows: T[]
  /** True when the merchant chose "Select all N" for the current filters — act on the whole
   *  filtered set server-side (send the same query the list uses). Reset whenever the query changes. */
  allMatching: boolean
  clear: () => void
}

export interface DataTableProps<T> {
  /** Rows; undefined while the first load is in flight. */
  data: T[] | undefined
  columns: DataTableColumn<T>[]
  getRowId: (row: T) => string
  /** Accessible name, e.g. "Orders". */
  caption: string
  /** First load (shows skeleton rows when there is no data yet). */
  loading?: boolean
  /** Background refetch (thin progress line; rows stay). */
  fetching?: boolean
  error?: unknown
  onRetry?: () => void
  /** Shown when data is empty (use EmptyState / NoResults). */
  empty?: ReactNode
  skeletonRows?: number

  /** Row checkboxes + bulk action bar. A function decides per row. */
  selectable?: boolean | ((row: T) => boolean)
  /** Controlled selection (optional). */
  selectedIds?: string[]
  onSelectedIdsChange?: (ids: string[]) => void
  /** Buttons for the floating bulk bar. */
  bulkActions?: (ctx: BulkActionContext<T>) => ReactNode
  /**
   * What the selection belongs to. Whenever it changes (compared by value), the selection and
   * "Select all N" are cleared. Defaults to the page's URL query (minus `cursor`), which is where
   * useListState keeps search/filters/sort/view — pass `list.query` (or any key) for tables whose
   * filters don't live in the URL. Rows that leave `data` are always dropped from the selection.
   */
  selectionScope?: unknown
  /** Total rows matching the current filters — enables "Select all N". */
  totalCount?: number
  /** Noun for counts ("order" → "3 orders selected"). */
  itemName?: { singular: string; plural: string }

  /** Controlled sort. With onSortChange the table does NOT sort itself (server sorting). */
  sort?: SortState | null
  onSortChange?: (s: SortState | null) => void
  /** Initial client sort. */
  defaultSort?: SortState

  /** Row navigation: click opens, ⌘/Ctrl-click or middle-click opens a new tab, Enter opens the focused row. */
  rowHref?: (row: T) => string | undefined
  onRowClick?: (row: T, e: MouseEvent | KeyboardEvent) => void
  rowClassName?: (row: T) => string | undefined
  /** Right-click menu for a row. */
  rowContextMenu?: (row: T) => Array<ContextMenuItemDef | 'separator'>
  /** Ids to flash (e.g. new orders arriving over SSE). */
  highlightIds?: string[]

  /** Persist column visibility + density in localStorage under this key (e.g. "orders"). */
  viewKey?: string
  density?: Density
  /** Columns & density menu in the header (default true). */
  viewOptions?: boolean
  /** Virtualize rows: true, false, or 'auto' (> 150 rows). */
  virtualize?: boolean | 'auto'
  /**
   * The element that scrolls the table when virtualized. Found automatically (the nearest ancestor
   * that actually scrolls — the shell's <main>, a fixed-height Card body — or the window); pass it
   * only for unusual layouts.
   */
  scrollElement?: HTMLElement | null
  /** Under the table (e.g. <LoadMore/>). */
  footer?: ReactNode
  className?: string
  /** Remove the outer border/radius (when the table sits flush inside a Card). */
  flush?: boolean
}

const features = tableFeatures({ rowSortingFeature, sortedRowModel: createSortedRowModel(), rowSelectionFeature, columnVisibilityFeature })
type F = typeof features

const hideBelowCls = { sm: 'hidden sm:table-cell', md: 'hidden md:table-cell', lg: 'hidden lg:table-cell', xl: 'hidden xl:table-cell' } as const
/** Minimum row heights (cells use them as min-height; taller content grows the row and is measured). */
const ROW_H: Record<Density, number> = { compact: 36, comfortable: 44 }
const EMPTY_IDS: string[] = []

function compare(a: unknown, b: unknown): number {
  const na = a === null || a === undefined || a === '', nb = b === null || b === undefined || b === ''
  if (na || nb) return na && nb ? 0 : na ? 1 : -1
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime()
  if (typeof a === 'number' && typeof b === 'number') return a - b
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b)
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
}

type ScrollTarget = HTMLElement | Window
const isScrollable = (el: Element) => { const oy = getComputedStyle(el).overflowY; return oy === 'auto' || oy === 'scroll' || oy === 'overlay' }
const isRootScroller = (el: Element | null) => !el || el === document.documentElement || el === document.body || el === document.scrollingElement

/**
 * The element whose vertical scrolling moves `el`: the nearest ancestor that is a scroll container
 * (overflow-y auto/scroll) AND taller inside than outside; the window when none is. Callers make
 * sure the table already has its full height when this runs (a first batch of rows + a spacer for
 * the rest), so "overflowing" is meaningful even on the first render — a shell <main> with a
 * 1,000-row table is always overflowing, a wrapper that merely has overflow-x never is.
 */
export function findScrollParent(el: HTMLElement | null): ScrollTarget {
  let p = el?.parentElement ?? null
  while (p && !isRootScroller(p)) {
    if (isScrollable(p) && p.scrollHeight > p.clientHeight + 1) return p
    p = p.parentElement
  }
  return window
}

/**
 * The admin table. Minimal use:
 * ```
 * <DataTable caption="Orders" data={q.data?.items} loading={q.isLoading} error={q.error} onRetry={q.refetch}
 *   getRowId={o => o.id} rowHref={o => `/orders/${o.id}`} columns={[
 *     { id: 'name', header: 'Order', cell: o => <span className="font-medium">{o.name}</span> },
 *     { id: 'createdAt', header: 'Date', sortable: true, sortDescFirst: true, cell: o => relativeTime(o.createdAt) },
 *     { id: 'total', header: 'Total', align: 'right', cell: o => money(o.total) },
 *   ]} sort={list.sort} onSortChange={list.setSort} selectable bulkActions={({ ids }) => <Button …/>} viewKey="orders"
 *   footer={<LoadMore …/>} />
 * ```
 */
export function DataTable<T extends Record<string, any>>(props: DataTableProps<T>) {
  const {
    data, columns, getRowId, caption, loading, fetching, error, onRetry, empty, skeletonRows = 8,
    selectable, selectedIds, onSelectedIdsChange, bulkActions, totalCount, itemName = { singular: 'item', plural: 'items' },
    sort, onSortChange, defaultSort, rowHref, onRowClick, rowClassName, rowContextMenu, highlightIds,
    viewKey, density: densityProp = 'comfortable', viewOptions = true, virtualize = 'auto', footer, className, flush,
    selectionScope, scrollElement,
  } = props
  const navigate = useNavigate()
  const location = useLocation()
  const serverSort = !!onSortChange

  // ── view prefs (persisted per viewKey) ────────────────────────────────────────────────
  const defaultVisibility = useMemo(() => Object.fromEntries(columns.filter(c => c.defaultHidden).map(c => [c.id, false])) as ColumnVisibilityState, [columns])
  const isVis = (v: unknown): v is ColumnVisibilityState => !!v && typeof v === 'object' && !Array.isArray(v) && Object.values(v as object).every(x => typeof x === 'boolean')
  const isDensity = (v: unknown): v is Density => v === 'compact' || v === 'comfortable'
  const [visibility, setVisibility] = useLocalStorage<ColumnVisibilityState>(viewKey ? `table:${viewKey}:columns` : null, defaultVisibility, isVis)
  const [density, setDensity] = useLocalStorage<Density>(viewKey ? `table:${viewKey}:density` : null, densityProp, isDensity)

  // ── selection ─────────────────────────────────────────────────────────────────────────
  // The selection only ever holds rows of the CURRENT result set: ids that leave `data` are dropped,
  // and a scope change (search/filters/sort/view) clears everything, "Select all N" included — so a
  // bulk action can never reach rows the merchant can't see or a filter they didn't select under.
  const scopeKey = useMemo(() => {
    if (selectionScope !== undefined) { try { return JSON.stringify(selectionScope) ?? '' } catch { return String(selectionScope) } }
    const q = new URLSearchParams(location.search); q.delete('cursor'); q.sort()
    return `${location.pathname}?${q}`
  }, [selectionScope, location.pathname, location.search])
  // Uncontrolled selection remembers its scope, so it reads as empty from the very render in which
  // the scope changes; a controlled one is cleared through onSelectedIdsChange right after.
  const [innerSel, setInnerSel] = useState<{ scope: string; ids: string[] }>({ scope: scopeKey, ids: EMPTY_IDS })
  const rawSel = selectedIds ?? (innerSel.scope === scopeKey ? innerSel.ids : EMPTY_IDS)
  const rowIdOf = useRef(getRowId)
  rowIdOf.current = getRowId
  const dataIds = useMemo(() => (data ? new Set(data.map(r => rowIdOf.current(r))) : null), [data])
  const selIds = useMemo(() => (dataIds ? rawSel.filter(id => dataIds.has(id)) : EMPTY_IDS), [rawSel, dataIds])
  const setSelIds = useCallback((ids: string[]) => {
    if (onSelectedIdsChange) onSelectedIdsChange(ids)
    if (!selectedIds) setInnerSel({ scope: scopeKey, ids })
  }, [onSelectedIdsChange, selectedIds, scopeKey])
  const rowSelection = useMemo<RowSelectionState>(() => Object.fromEntries(selIds.map(id => [id, true])), [selIds])
  const [activeRow, setActiveRow] = useState(0)
  // "Select all N" remembers the scope it was chosen in, likewise.
  const [allMatchingScope, setAllMatchingScope] = useState<string | null>(null)
  const allMatching = allMatchingScope !== null && allMatchingScope === scopeKey
  const setAllMatching = useCallback((v: boolean) => setAllMatchingScope(v ? scopeKey : null), [scopeKey])
  // Write the pruned selection back so controlled parents see it too (once per distinct result, so a
  // parent that re-renders with a fresh array can't loop us). Declared BEFORE the scope reset so
  // that, in a commit where both run, the reset is the last write.
  const lastPruned = useRef('')
  useEffect(() => {
    if (!dataIds || selIds.length === rawSel.length) { lastPruned.current = ''; return }
    const key = selIds.join('\u0000')
    if (key === lastPruned.current) return
    lastPruned.current = key
    setSelIds(selIds)
  }, [selIds, rawSel.length, dataIds, setSelIds])
  const lastScope = useRef(scopeKey)
  useEffect(() => {
    if (lastScope.current === scopeKey) return
    lastScope.current = scopeKey
    setAllMatchingScope(null)
    setActiveRow(0)
    if (selectedIds?.length || innerSel.ids.length) setSelIds([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey])
  useEffect(() => { if (!selIds.length && allMatchingScope !== null) setAllMatchingScope(null) }, [selIds.length, allMatchingScope])

  // ── sorting ───────────────────────────────────────────────────────────────────────────
  const [innerSort, setInnerSort] = useState<SortingState>(defaultSort ? [defaultSort] : [])
  const sorting: SortingState = serverSort ? (sort ? [sort] : []) : innerSort

  // ── columns → TanStack defs ───────────────────────────────────────────────────────────
  const colDefs = useMemo(() => {
    const helper = createColumnHelper<F, T>()
    return helper.columns(columns.map((c, i) => helper.accessor((row: T) => (c.sortValue ? c.sortValue(row) : null) as any, {
      id: c.id,
      header: () => null,
      enableSorting: serverSort ? !!c.sortable : !!(c.sortValue && c.sortable !== false),
      enableHiding: i === 0 ? false : c.hideable !== false,
      sortDescFirst: !!c.sortDescFirst,
      sortUndefined: 'last',
      sortFn: (a: Row<F, T>, b: Row<F, T>, id: string) => compare(a.getValue(id), b.getValue(id)),
    } as any)))
  }, [columns, serverSort])
  const byId = useMemo(() => new Map(columns.map(c => [c.id, c])), [columns])

  const table = useTable<F, T>({
    features,
    columns: colDefs as any,
    data: data ?? (EMPTY_ROWS as T[]),
    getRowId: (r: T) => getRowId(r),
    state: { rowSelection, sorting, columnVisibility: visibility },
    onRowSelectionChange: (u: any) => {
      const next: RowSelectionState = typeof u === 'function' ? u(rowSelection) : u
      setSelIds(Object.keys(next).filter(k => next[k]))
      setAllMatching(false)
    },
    onSortingChange: (u: any) => {
      const next: SortingState = typeof u === 'function' ? u(sorting) : u
      if (serverSort) onSortChange!(next[0] ?? null)
      else setInnerSort(next)
    },
    onColumnVisibilityChange: (u: any) => setVisibility(typeof u === 'function' ? u(visibility) : u),
    manualSorting: serverSort,
    enableMultiSort: false,
    enableSortingRemoval: false,
    enableRowSelection: typeof selectable === 'function' ? ((row: Row<F, T>) => (selectable as (r: T) => boolean)(row.original)) : !!selectable,
  } as any)

  const rows = table.getRowModel().rows
  // "Select all N" keeps meaning all: rows loaded later (Load more) arrive selected.
  useEffect(() => {
    if (!allMatching || !data) return
    const all = rows.filter(r => r.getCanSelect()).map(r => r.id)
    if (all.some(id => !rowSelection[id])) setSelIds(all)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMatching, data])
  const visibleCols = table.getVisibleLeafColumns().map(c => byId.get(c.id)).filter(Boolean) as DataTableColumn<T>[]
  const canSelect = !!selectable
  const extraCols = (canSelect ? 1 : 0) + (viewOptions ? 1 : 0)
  const colSpan = visibleCols.length + extraCols
  const rowH = ROW_H[density]

  // ── virtualization ────────────────────────────────────────────────────────────────────
  // Until the scroll container is known, render a first screenful of rows plus a spacer for the
  // rest, so the table already has its full height: the container is then simply the nearest
  // ancestor that overflows (the shell's <main>, a fixed-height box) or the window. A capture-phase
  // scroll listener corrects the choice if the merchant scrolls some other ancestor.
  const wrapRef = useRef<HTMLDivElement>(null)
  const tbodyRef = useRef<HTMLTableSectionElement>(null)
  const virtualOn = virtualize === true || (virtualize === 'auto' && rows.length > 150)
  const [autoTarget, setAutoTarget] = useState<ScrollTarget | null>(null)
  const target: ScrollTarget | null = virtualOn ? (scrollElement ?? autoTarget) : null
  const targetRef = useRef(target)
  targetRef.current = target
  const [margin, setMargin] = useState(0)
  const measureMargin = useCallback(() => {
    const t = targetRef.current, body = tbodyRef.current
    if (!t || !body) return
    const top = t === window
      ? body.getBoundingClientRect().top + window.scrollY
      : body.getBoundingClientRect().top - (t as HTMLElement).getBoundingClientRect().top + (t as HTMLElement).scrollTop
    setMargin(m => (Math.abs(m - top) > 0.5 ? top : m))
  }, [])
  useLayoutEffect(() => {
    if (!virtualOn || scrollElement) return
    setAutoTarget(findScrollParent(wrapRef.current))
    const onScroll = (e: Event) => {
      const wrap = wrapRef.current
      if (!wrap) return
      const t = e.target
      const next: ScrollTarget | null = t === document || isRootScroller(t as Element) ? window
        : t instanceof HTMLElement && t !== wrap && t.contains(wrap) && t.scrollHeight > t.clientHeight + 1 ? t : null
      if (next && next !== targetRef.current) setAutoTarget(next)
    }
    document.addEventListener('scroll', onScroll, { capture: true, passive: true })
    return () => document.removeEventListener('scroll', onScroll, { capture: true } as EventListenerOptions)
  }, [virtualOn, scrollElement])
  useLayoutEffect(() => {
    if (!target) return
    measureMargin()
    const ro = new ResizeObserver(measureMargin)
    if (target !== window) ro.observe(target as HTMLElement)
    if (wrapRef.current?.parentElement) ro.observe(wrapRef.current.parentElement)
    return () => ro.disconnect()
  }, [target, rows.length, measureMargin])
  const offsetOf = (t: ScrollTarget | null) => (!t ? 0 : t === window ? window.scrollY : (t as HTMLElement).scrollTop)
  const onWindow = target === window
  const virtualizer = useVirtualizer<any, HTMLTableRowElement>({
    count: target ? rows.length : 0,
    getScrollElement: () => targetRef.current as any,
    observeElementRect: (onWindow ? observeWindowRect : observeElementRect) as any,
    observeElementOffset: (onWindow ? observeWindowOffset : observeElementOffset) as any,
    scrollToFn: (onWindow ? windowScroll : elementScroll) as any,
    initialOffset: () => offsetOf(targetRef.current),
    estimateSize: () => rowH,
    overscan: 12,
    scrollMargin: margin,
    getItemKey: i => rows[i]?.id ?? i,
  })
  // A new scroll target must start from ITS offset, not the previous target's (the virtualizer
  // would otherwise scroll it back to the old position when it re-subscribes).
  const lastTarget = useRef<ScrollTarget | null>(null)
  if (lastTarget.current !== target) { lastTarget.current = target; virtualizer.scrollOffset = null }
  const firstBatch = Math.min(rows.length, Math.ceil((typeof window !== 'undefined' ? window.innerHeight : 900) / rowH) + 12)
  const vItems = virtualOn && target ? virtualizer.getVirtualItems() : []
  const padTop = virtualOn && vItems.length ? vItems[0].start - margin : 0
  const padBottom = !virtualOn ? 0
    : !target ? (rows.length - firstBatch) * rowH
    : vItems.length ? virtualizer.getTotalSize() - (vItems[vItems.length - 1].end - margin) : 0
  const renderRows = !virtualOn ? rows.map((row, i) => ({ row, i }))
    : !target ? rows.slice(0, firstBatch).map((row, i) => ({ row, i }))
    : vItems.filter(v => rows[v.index]).map(v => ({ row: rows[v.index], i: v.index }))

  // ── horizontal overflow: only become a scroll container when needed (keeps sticky header) ─
  const [overflowX, setOverflowX] = useState(false)
  useLayoutEffect(() => {
    const wrap = wrapRef.current
    if (!wrap) return
    const check = () => { const t = wrap.querySelector('table'); if (t) setOverflowX(t.scrollWidth > wrap.clientWidth + 1) }
    check()
    const ro = new ResizeObserver(check)
    ro.observe(wrap)
    const t = wrap.querySelector('table'); if (t) ro.observe(t)
    return () => ro.disconnect()
  }, [visibleCols.length, rows.length > 0])

  // ── keyboard row navigation ───────────────────────────────────────────────────────────
  // Clamp: after a filter leaves fewer rows, one row must still be the Tab stop.
  const activeIdx = Math.max(0, Math.min(activeRow, rows.length - 1))
  const focusRow = (i: number) => {
    const clamped = Math.max(0, Math.min(rows.length - 1, i))
    setActiveRow(clamped)
    if (virtualOn) virtualizer.scrollToIndex(clamped, { align: 'auto' })
    requestAnimationFrame(() => tbodyRef.current?.querySelector<HTMLTableRowElement>(`tr[data-index="${clamped}"]`)?.focus({ preventScroll: !virtualOn ? false : true }))
  }
  const open = (row: T, e: MouseEvent | KeyboardEvent, newTab = false) => {
    const href = rowHref?.(row)
    if (href) {
      if (newTab) window.open(href, '_blank', 'noopener')
      else navigate(href)
    }
    onRowClick?.(row, e)
  }
  const onBodyKey = (e: KeyboardEvent<HTMLTableSectionElement>) => {
    const tr = (e.target as HTMLElement).closest('tr[data-index]') as HTMLTableRowElement | null
    if (!tr || e.target !== tr) return
    const i = Number(tr.dataset.index)
    const row = rows[i]
    if (!row) return
    switch (e.key) {
      case 'ArrowDown': case 'j': e.preventDefault(); focusRow(i + 1); break
      case 'ArrowUp': case 'k': e.preventDefault(); focusRow(i - 1); break
      case 'Home': e.preventDefault(); focusRow(0); break
      case 'End': e.preventDefault(); focusRow(rows.length - 1); break
      case 'Enter': if (rowHref || onRowClick) { e.preventDefault(); open(row.original, e, e.metaKey || e.ctrlKey) } break
      case ' ': case 'x':
        if (canSelect && row.getCanSelect()) { e.preventDefault(); row.getToggleSelectedHandler()({ target: { checked: !row.getIsSelected() }, shiftKey: e.shiftKey }) }
        break
    }
  }
  const interactive = (t: EventTarget | null) => !!(t as HTMLElement | null)?.closest?.('a,button,input,label,select,textarea,[role="checkbox"],[role="button"],[role="menuitem"],[data-row-click-ignore]')
  const onRowClickCapture = (row: Row<F, T>, e: MouseEvent<HTMLTableRowElement>) => {
    if (interactive(e.target) || !(rowHref || onRowClick)) return
    if (window.getSelection()?.toString()) return   // selecting text, not navigating
    open(row.original, e, e.metaKey || e.ctrlKey || e.shiftKey)
  }

  // ── states ────────────────────────────────────────────────────────────────────────────
  const firstLoad = (loading && !data) || (!data && !error)
  const showError = !!error && !(data && data.length)
  const isEmpty = !firstLoad && !showError && !!data && data.length === 0
  const highlight = useMemo(() => new Set(highlightIds ?? []), [highlightIds])

  const allPage = table.getIsAllRowsSelected()
  const somePage = table.getIsSomeRowsSelected()
  const selectedCount = allMatching && totalCount ? totalCount : selIds.length
  const selectedRows = useMemo(() => (data ?? []).filter(r => rowSelection[getRowId(r)]), [data, rowSelection, getRowId])
  const noun = (n: number) => (n === 1 ? itemName.singular : itemName.plural)

  const alignCls = (a?: 'left' | 'right' | 'center') => (a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left')
  const cellPad = density === 'compact' ? 'h-9 py-1' : 'h-11 py-2'

  return (
    <div className={cn('relative flex min-w-0 flex-col', !flush && 'overflow-clip rounded-card border border-border bg-surface shadow-card', className)}>
      {fetching && data && <div className="absolute inset-x-0 top-0 z-20 h-0.5 overflow-hidden"><div className="h-full w-2/5 animate-indeterminate bg-primary" /></div>}
      {/* Outside the (possibly horizontally scrolling) table so it always wraps within the visible width. */}
      {allPage && canSelect && totalCount !== undefined && totalCount > rows.length && (
        <div role="status" className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 border-b border-border bg-primary-soft/60 px-4 py-2 text-center text-sm text-text">
          {allMatching
            ? <><span>All {fmtNumber(totalCount)} {noun(totalCount)} are selected.</span><button type="button" className="font-medium text-primary-strong hover:underline" onClick={() => { setAllMatching(false); setSelIds([]) }}>Clear selection</button></>
            : <><span>All {rows.length} {noun(rows.length)} on this page are selected.</span><button type="button" className="font-medium text-primary-strong hover:underline" onClick={() => setAllMatching(true)}>Select all {fmtNumber(totalCount)} {noun(totalCount)}</button></>}
        </div>
      )}
      <div ref={wrapRef} className={cn('min-w-0', overflowX ? 'overflow-x-auto' : 'overflow-x-clip')}>
        <table aria-label={caption} className="w-full border-separate border-spacing-0 text-base">
          <thead>
            <tr>
              {canSelect && (
                <th scope="col" className="sticky top-0 z-10 w-10 border-b border-border bg-surface-sunken py-0 pr-0 pl-4 dark:bg-[#131316]">
                  <Checkbox aria-label="Select all rows" checked={allPage ? true : somePage ? 'indeterminate' : false}
                    disabled={!rows.length} onCheckedChange={v => {
                      // Select every selectable loaded row, or clear the WHOLE selection (not just the visible rows).
                      setAllMatching(false)
                      setSelIds(v === true ? rows.filter(r => r.getCanSelect()).map(r => r.id) : [])
                    }} />
                </th>
              )}
              {visibleCols.map((c, ci) => {
                const col = table.getColumn(c.id)
                const canSort = !!col?.getCanSort()
                const dir = col?.getIsSorted()
                return (
                  <th key={c.id} scope="col" aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : undefined}
                    style={{ width: c.width, minWidth: c.minWidth }}
                    className={cn('sticky top-0 z-10 h-9 border-b border-border bg-surface-sunken px-3 text-sm font-medium whitespace-nowrap text-text-muted dark:bg-[#131316]',
                      ci === 0 && !canSelect && 'pl-4', alignCls(c.align), c.hideBelow && hideBelowCls[c.hideBelow], c.headerClassName)}>
                    {canSort ? (
                      <button type="button" onClick={col!.getToggleSortingHandler()}
                        className={cn('group inline-flex items-center gap-1 rounded-[4px] hover:text-text focus-visible:outline-2 focus-visible:outline-focus', c.align === 'right' && 'flex-row-reverse', dir && 'text-text')}>
                        {c.header}
                        {dir === 'asc' ? <ArrowUp className="size-3.5" /> : dir === 'desc' ? <ArrowDown className="size-3.5" /> : <ArrowUpDown className="size-3.5 opacity-0 transition-opacity group-hover:opacity-60 group-focus-visible:opacity-60" />}
                      </button>
                    ) : c.header}
                  </th>
                )
              })}
              {viewOptions && (
                <th scope="col" className="sticky top-0 z-10 w-10 border-b border-border bg-surface-sunken pr-2 text-right dark:bg-[#131316]">
                  <DropdownMenu trigger={<Button variant="ghost" size="sm" icon={<Columns3 />} tooltip="Columns & density" aria-label="Columns and density" className="size-7" />}>
                    <MenuLabel>Density</MenuLabel>
                    <MenuRadioGroup value={density} onValueChange={setDensity} options={[{ value: 'comfortable', label: 'Comfortable' }, { value: 'compact', label: 'Compact' }]} />
                    <MenuSeparator />
                    <MenuLabel>Columns</MenuLabel>
                    {table.getAllLeafColumns().filter(c => c.getCanHide()).map(c => (
                      <MenuCheckboxItem key={c.id} checked={c.getIsVisible()} onCheckedChange={v => c.toggleVisibility(v)} onSelect={e => e.preventDefault()}>
                        {byId.get(c.id)?.header}
                      </MenuCheckboxItem>
                    ))}
                  </DropdownMenu>
                </th>
              )}
            </tr>
          </thead>
          <tbody ref={tbodyRef} onKeyDown={onBodyKey}>
            {firstLoad && Array.from({ length: skeletonRows }, (_, i) => (
              <tr key={`sk${i}`} aria-hidden>
                {canSelect && <td className="border-b border-border py-0 pr-0 pl-4" style={{ height: rowH }}><Skeleton className="size-4" rounded="sm" /></td>}
                {visibleCols.map((c, ci) => (
                  <td key={c.id} className={cn('border-b border-border px-3', ci === 0 && !canSelect && 'pl-4', c.hideBelow && hideBelowCls[c.hideBelow])} style={{ height: rowH }}>
                    <Skeleton className={cn('h-3', c.align === 'right' && 'ml-auto')} style={{ width: `${[72, 56, 64, 48, 80, 40][(i + ci) % 6]}%` }} />
                  </td>
                ))}
                {viewOptions && <td className="border-b border-border" />}
              </tr>
            ))}
            {showError && <tr><td colSpan={colSpan}><ErrorState error={error} onRetry={onRetry} compact /></td></tr>}
            {isEmpty && <tr><td colSpan={colSpan}>{empty ?? <EmptyState compact illustration={<Inbox />} title={`No ${itemName.plural} yet`} />}</td></tr>}
            {!firstLoad && !showError && padTop > 0 && <tr aria-hidden><td colSpan={colSpan} style={{ height: padTop, padding: 0, border: 0 }} /></tr>}
            {!firstLoad && !showError && renderRows.map(({ row, i }) => {
              const selected = row.getIsSelected()
              const clickable = !!(rowHref || onRowClick)
              const tr = (
                <DataRow<T>
                  key={row.id}
                  row={row}
                  index={i}
                  cols={visibleCols}
                  selected={selected}
                  canSelect={canSelect}
                  selectableRow={row.getCanSelect()}
                  viewOptions={viewOptions}
                  active={i === activeIdx}
                  clickable={clickable}
                  highlight={highlight.has(row.id)}
                  className={rowClassName?.(row.original)}
                  cellPad={cellPad}
                  measureRef={virtualOn && target ? virtualizer.measureElement : undefined}
                  onClick={e => onRowClickCapture(row, e)}
                  onAuxClick={e => { if (e.button === 1 && rowHref && !interactive(e.target)) { const h = rowHref(row.original); if (h) window.open(h, '_blank', 'noopener') } }}
                  onFocus={() => setActiveRow(i)}
                  onToggle={e => row.getToggleSelectedHandler()({ target: { checked: !selected }, shiftKey: e.shiftKey, nativeEvent: e.nativeEvent })}
                />
              )
              return rowContextMenu ? <ContextMenu key={row.id} items={rowContextMenu(row.original)}>{tr}</ContextMenu> : tr
            })}
            {!firstLoad && !showError && padBottom > 0 && <tr aria-hidden><td colSpan={colSpan} style={{ height: padBottom, padding: 0, border: 0 }} /></tr>}
          </tbody>
        </table>
      </div>
      {footer && <div className="border-t border-border first:border-t-0">{footer}</div>}
      {canSelect && selIds.length > 0 && typeof document !== 'undefined' && createPortal(
        <div className="pointer-events-none fixed inset-x-3 bottom-3 z-40 flex justify-center pb-[env(safe-area-inset-bottom)] sm:bottom-5 lg:left-[calc(var(--shell-sidebar-w,0px)+12px)]">
          {/* Clear + count stay put; only the actions scroll sideways when the bar is narrower than they are (phones). */}
          <div role="toolbar" aria-label="Bulk actions" className="pointer-events-auto flex max-w-full animate-bar-in items-center gap-2 rounded-[14px] border border-border bg-surface-raised py-2 pr-2 pl-2 shadow-bar">
            <Button variant="ghost" size="md" icon={<X />} tooltip="Clear selection" aria-label="Clear selection" className="shrink-0" onClick={() => { setSelIds([]); setAllMatching(false) }} />
            <span className="shrink-0 pr-1 text-base font-medium whitespace-nowrap text-text tabular">{fmtNumber(selectedCount)} selected</span>
            <span className="h-5 w-px shrink-0 bg-border" />
            <div className="flex min-w-0 items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {bulkActions?.({ ids: selIds, rows: selectedRows, allMatching, clear: () => { setSelIds([]); setAllMatching(false) } })}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
const EMPTY_ROWS: unknown[] = []

interface DataRowProps<T extends Record<string, any>> {
  row: Row<F, T>
  index: number
  cols: DataTableColumn<T>[]
  selected: boolean
  canSelect: boolean
  selectableRow: boolean
  viewOptions: boolean
  active: boolean
  clickable: boolean
  highlight: boolean
  className?: string
  cellPad: string
  /** Virtual rows report their real height (multi-line cells, thumbnails). */
  measureRef?: (el: HTMLTableRowElement | null) => void
  onClick: (e: MouseEvent<HTMLTableRowElement>) => void
  onAuxClick: (e: MouseEvent<HTMLTableRowElement>) => void
  onFocus: () => void
  onToggle: (e: MouseEvent<HTMLButtonElement>) => void
}

function DataRowInner<T extends Record<string, any>>({ row, index, cols, selected, canSelect, selectableRow, viewOptions, active, clickable, highlight, className, cellPad, measureRef, onClick, onAuxClick, onFocus, onToggle, ...rest }: DataRowProps<T>) {
  const alignCls = (a?: 'left' | 'right' | 'center') => (a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left')
  // A row wrapped in <ContextMenu> receives the trigger's ref through props (React 19): keep both.
  const { ref: slotRef, ...others } = rest as { ref?: ((el: HTMLTableRowElement | null) => void) | { current: HTMLTableRowElement | null } | null }
  const setRef = (el: HTMLTableRowElement | null) => {
    measureRef?.(el)
    if (typeof slotRef === 'function') slotRef(el)
    else if (slotRef && typeof slotRef === 'object') slotRef.current = el
  }
  return (
    <tr
      {...others}
      ref={setRef}
      data-index={index}
      tabIndex={active ? 0 : -1}
      aria-selected={canSelect ? selected : undefined}
      onClick={onClick}
      onAuxClick={onAuxClick}
      onFocus={e => { if (e.target === e.currentTarget) onFocus() }}
      className={cn(
        'group/row outline-none transition-colors duration-100',
        clickable && 'cursor-pointer',
        selected ? 'bg-primary-soft/50 hover:bg-primary-soft/70' : 'hover:bg-surface-hover',
        'focus-visible:bg-surface-hover focus-visible:shadow-[inset_2px_0_0_var(--focus)]',
        highlight && 'animate-row-flash',
        className,
      )}
    >
      {canSelect && (
        <td className={cn('w-10 border-b border-border py-0 pr-0 pl-4 align-middle group-last/row:border-b-0', cellPad)} onClick={e => e.stopPropagation()}>
          {selectableRow && <Checkbox aria-label="Select row" checked={selected} onClick={e => { e.preventDefault(); onToggle(e) }} />}
        </td>
      )}
      {cols.map((c, ci) => (
        <td key={c.id}
          className={cn('border-b border-border px-3 align-middle text-text group-last/row:border-b-0', cellPad,
            ci === 0 && !canSelect && 'pl-4', alignCls(c.align), c.align === 'right' && 'tabular',
            c.hideBelow && hideBelowCls[c.hideBelow], c.className)}>
          {c.truncate
            ? <div className="min-w-0 truncate" style={{ maxWidth: c.maxWidth ?? 280 }}>{c.cell(row.original, index)}</div>
            : c.cell(row.original, index)}
        </td>
      ))}
      {viewOptions && <td className="border-b border-border group-last/row:border-b-0" />}
    </tr>
  )
}
const DataRow = DataRowInner
